import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AuthenticationService } from '../authentication/authentication.service';
import { OverheadCategory } from '../models/OverheadCategory';
import { VAT } from '../models/VAT';
import { ContactMapper } from '../models/yuki/ContactMapper';
import YukiDocument from '../models/yuki/YukiDocument';
import { YukiFile } from '../models/yuki/YukiFile';
import YukiFolder from '../models/yuki/YukiFolder';
import { YukiMarGlAccount } from '../models/yuki/YukiMarGlAccount';
import { YukiPage } from '../models/yuki/YukiPage';
import { YukiSettings } from '../models/yuki/YukiSettings';
import { InvoiceDTO } from './create-new-invoice-modal/create-new-invoice-modal.component';
import { YukiDocumentDTO } from './yuki-detail-modal/yuki-detail-modal.component';

export type YukiAutoAllocationResult = {
    documentUuid: string;
    vatUpdated: boolean;
    allocations: any[];
    suggestedAllocationText?: string | null;
    noMatchReason?: string | null;
    aiPrompt?: string | null;
    aiResponse?: string | null;
};

export type YukiAutoAllocateContextRequest = {
    vatOnly?: boolean;
    linkOnly?: boolean;
    includeFleet?: boolean;
    aiHint?: string | null;
};

@Injectable({
    providedIn: 'root',
})
export class YukiService {
    private readonly marAccountsCacheKey = 'resumo.yuki.mar-accounts.v2';
    private readonly marAccountsCacheTtlMs = 1000 * 60 * 60;
    httpOptions = {};

    constructor(private httpClient: HttpClient, private authenticationService: AuthenticationService) {
        this.httpOptions = authenticationService.getHeaders();
    }

    getFolders(startDate?: string, endDate?: string): Observable<YukiFolder[]> {
        let url = environment.baseUrl + '/yuki/folders';
        const params: string[] = [];
        if (startDate) params.push(`startDate=${startDate}`);
        if (endDate) params.push(`endDate=${endDate}`);
        if (params.length > 0) url += '?' + params.join('&');
        return this.httpClient.get<YukiFolder[]>(url, this.httpOptions);
    }

    getDocumentsByFolder(folderId: number, pageNumber: number, recordAmount: number): Observable<YukiPage> {
        return this.httpClient
            .get<YukiPage>(environment.baseUrl + `/yuki/documents?folderId=${folderId}&pageNumber=${pageNumber}&recordAmount=${recordAmount}`, this.httpOptions)
            .pipe(
                map((page) => {
                    page.documents.forEach((document) => {
                        this.normalizeDocumentProperties(document);
                        document.vat = VAT.findVAT(document.vattext);
                    });
                    return page;
                }),
            );
    }

    getAllDocumentsByFolder(folderId: number): Observable<YukiDocument[]> {
        return this.httpClient.get<YukiDocument[]>(environment.baseUrl + `/yuki/documents/all?folderId=${folderId}`, this.httpOptions).pipe(
            map((documents) => {
                documents.forEach((document) => {
                    // Normalize property names from backend (isX -> x) first
                    this.normalizeDocumentProperties(document);
                    document.vat = VAT.findVAT(document.vattext);
                });
                return documents;
            }),
        );
    }

    getSalesDocumentsByFolder(folderId: number): Observable<YukiDocument[]> {
        return this.httpClient.get<YukiDocument[]>(environment.baseUrl + `/yuki/documents/sales?folderId=${folderId}`, this.httpOptions).pipe(
            map((documents) => {
                documents.forEach((document) => {
                    this.normalizeDocumentProperties(document);
                    document.vat = VAT.findVAT(document.vattext);
                });
                return documents;
            }),
        );
    }

    private normalizeDocumentProperties(document: any): void {
        // Handle backend property naming inconsistency (isX vs x)
        if (document.isVerified !== undefined && document.verified === undefined) {
            document.verified = document.isVerified;
        }
        if (document.isFlagged !== undefined && document.flagged === undefined) {
            document.flagged = document.isFlagged;
        }
        if (document.isVisible !== undefined && document.visible === undefined) {
            document.visible = document.isVisible;
        }
        // Handle VATText vs vattext
        if (document.VATText !== undefined && document.vattext === undefined) {
            document.vattext = document.VATText;
        }
        if (document.VATAmount !== undefined && document.vatamount === undefined) {
            document.vatamount = document.VATAmount;
        }
    }

    fetchDocumentById(id: string): Observable<YukiDocument> {
        return this.httpClient.get<YukiDocument>(`${environment.baseUrl}/yuki/document/${id}`, this.httpOptions).pipe(
            map((document) => {
                this.normalizeDocumentProperties(document);
                document.vat = VAT.findVAT(document.vattext);
                return document;
            }),
        );
    }

    fetchFile(yukiId: string): Observable<YukiFile> {
        return this.httpClient.get<YukiFile>(`${environment.baseUrl}/yuki/document/file/${yukiId}`, this.httpOptions);
    }

    refreshFile(yukiId: string) {
        return this.httpClient.patch<YukiFile>(`${environment.baseUrl}/yuki/document/file/${yukiId}`, {}, this.httpOptions);
    }

    fetchYukiSettings(): Observable<YukiSettings> {
        return this.httpClient.get<YukiSettings>(`${environment.baseUrl}/yuki/settings`, this.httpOptions);
    }

    updateYukiSettings(uuid: string, settings: YukiSettings): Observable<YukiSettings> {
        return this.httpClient.patch<YukiSettings>(`${environment.baseUrl}/yuki/settings/${uuid}`, settings, this.httpOptions);
    }

    updateVATOfYukiDocument(uuid: string, body: YukiDocumentDTO) {
        return this.httpClient.put<YukiDocument>(`${environment.baseUrl}/yuki/document/${uuid}/vat`, body, this.httpOptions).pipe(
            map((document) => {
                document.vat = VAT.findVAT(document.vattext);
                return document;
            }),
        );
    }

    verifyYukiDocument(uuid: string) {
        return this.httpClient.put(`${environment.baseUrl}/yuki/document/${uuid}/verify`, {}, this.httpOptions);
    }

    autoAllocateDocument(uuid: string, vatOnly: boolean = false, linkOnly: boolean = false, includeFleet: boolean = false): Observable<YukiAutoAllocationResult> {
        return this.autoAllocateDocumentWithContext(uuid, {
            vatOnly,
            linkOnly,
            includeFleet,
        });
    }

    autoAllocateDocumentWithContext(uuid: string, context: YukiAutoAllocateContextRequest): Observable<YukiAutoAllocationResult> {
        return this.httpClient.post<YukiAutoAllocationResult>(
            `${environment.baseUrl}/yuki/document/${uuid}/auto-allocate`,
            {
                vatOnly: context?.vatOnly ?? false,
                linkOnly: context?.linkOnly ?? false,
                includeFleet: context?.includeFleet ?? false,
                aiHint: context?.aiHint ?? null,
            },
            this.httpOptions,
        );
    }

    getLogbookItemsForObject(objectId: string): Observable<any[]> {
        return this.httpClient.get<any[]>(`${environment.baseUrl}/logbook/${objectId}`, this.httpOptions);
    }

    autoLinkUnlinkedInvoices(folderId: number, startDate?: string, endDate?: string): Observable<any> {
        const queryStart = startDate ? `&startDate=${encodeURIComponent(startDate)}` : '';
        const queryEnd = endDate ? `&endDate=${encodeURIComponent(endDate)}` : '';
        return this.httpClient.post<any>(
            `${environment.baseUrl}/yuki/documents/auto-link-unlinked?folderId=${folderId}${queryStart}${queryEnd}`,
            {},
            this.httpOptions,
        );
    }

    startAutoLinkUnlinkedInvoices(folderId: number, startDate?: string, endDate?: string): Observable<any> {
        const queryStart = startDate ? `&startDate=${encodeURIComponent(startDate)}` : '';
        const queryEnd = endDate ? `&endDate=${encodeURIComponent(endDate)}` : '';
        return this.httpClient.post<any>(
            `${environment.baseUrl}/yuki/documents/auto-link-unlinked/start?folderId=${folderId}${queryStart}${queryEnd}`,
            {},
            this.httpOptions,
        );
    }

    getAutoLinkUnlinkedInvoicesStatus(): Observable<any> {
        return this.httpClient.get<any>(`${environment.baseUrl}/yuki/documents/auto-link-unlinked/status`, this.httpOptions);
    }

    syncDocuments() {
        return this.httpClient.post<any>(`${environment.baseUrl}/yuki/sync`, {}, this.httpOptions);
    }

    getSyncStatus() {
        return this.httpClient.get<any>(`${environment.baseUrl}/yuki/sync/status`, this.httpOptions);
    }

    stopSync() {
        return this.httpClient.post<any>(`${environment.baseUrl}/yuki/sync/stop`, {}, this.httpOptions);
    }

    updateVisibilityYukiDocument(yukiId: string) {
        return this.httpClient.patch<void>(`${environment.baseUrl}/yuki/visible/${yukiId}`, {}, this.httpOptions);
    }

    flagYukiDocument(yukiId: string) {
        return this.httpClient.patch<void>(`${environment.baseUrl}/yuki/flag/${yukiId}`, {}, this.httpOptions);
    }

    getOverheadCategoryById(id: string): Observable<OverheadCategory> {
        return this.httpClient.get<OverheadCategory>(`${environment.baseUrl}/overhead-categories/${id}`, this.httpOptions);
    }

    getAllOverheadCategories(): Observable<OverheadCategory[]> {
        return this.httpClient.get<OverheadCategory[]>(`${environment.baseUrl}/overhead-categories/all`, this.httpOptions);
    }

    addOverheadCategories(overheadCategories: OverheadCategory[]): Observable<OverheadCategory[]> {
        return this.httpClient.post<OverheadCategory[]>(`${environment.baseUrl}/overhead-categories`, overheadCategories, this.httpOptions);
    }

    deleteOverheadCategory(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${environment.baseUrl}/overhead-categories/${id}`, this.httpOptions);
    }

    updateOverheadCategory(overheadCategory: OverheadCategory): Observable<OverheadCategory> {
        return this.httpClient.put<OverheadCategory>(`${environment.baseUrl}/overhead-categories/${overheadCategory.id}`, overheadCategory, this.httpOptions);
    }

    setOverheadCategoryHidden(id: string, hidden: boolean): Observable<OverheadCategory> {
        return this.httpClient.patch<OverheadCategory>(`${environment.baseUrl}/overhead-categories/${id}/hidden?hidden=${hidden}`, {}, this.httpOptions);
    }

    refreshDocumentFromYukiAPI(yukiId: string) {
        return this.httpClient.patch<YukiDocument>(`${environment.baseUrl}/yuki/document/${yukiId}`, {}, this.httpOptions).pipe(
            map((document) => {
                document.vat = VAT.findVAT(document.vattext);
                return document;
            }),
        );
    }

    deleteDocument(yukiId: string) {
        return this.httpClient.delete(`${environment.baseUrl}/yuki/document/${yukiId}`, this.httpOptions);
    }

    getRemovedYukiDocuments(): Observable<YukiDocument[]> {
        return this.httpClient.get<YukiDocument[]>(`${environment.baseUrl}/yuki/get-removed`, this.httpOptions);
    }

    checkRemovedYukiDocuments(): Observable<YukiDocument[]> {
        return this.httpClient.post<YukiDocument[]>(`${environment.baseUrl}/yuki/check-removed`, {}, this.httpOptions);
    }

    getMappedContacts(): Observable<ContactMapper[]> {
        return this.httpClient.get<ContactMapper[]>(`${environment.baseUrl}/yuki/map-contacts`, this.httpOptions);
    }

    addMappedContact(yukiContact: string, resumoContactId: string): Observable<ContactMapper> {
        const body = {
            yukiContact: yukiContact,
            resumoContactId: resumoContactId,
        };
        return this.httpClient.post<ContactMapper>(`${environment.baseUrl}/yuki/map-contacts`, body, this.httpOptions);
    }

    mapContactsForAllExistingDocuments(): Observable<void> {
        return this.httpClient.patch<void>(`${environment.baseUrl}/yuki/map-contacts`, {}, this.httpOptions);
    }

    deleteMappedContact(mappedContactId: string): Observable<void> {
        return this.httpClient.delete<void>(`${environment.baseUrl}/yuki/map-contacts/${mappedContactId}`, this.httpOptions);
    }

    createNewInvoice(invoiceDTO: InvoiceDTO) {
        return this.httpClient.post<YukiDocument>(`${environment.baseUrl}/yuki/create-invoice`, invoiceDTO, this.httpOptions);
    }

    getMarAccounts(): Observable<YukiMarGlAccount[]> {
        const cachedAccounts = this.getCachedMarAccounts();
        if (cachedAccounts) {
            console.log('[MAR-LINKING][Service] using cached MAR accounts', { count: cachedAccounts.length });
            return this.getMarAccountMappings().pipe(map((mappings) => this.applyMarAccountMappings(cachedAccounts, mappings)));
        }

        console.log('[MAR-LINKING][Service] loading MAR accounts from API /yuki/mar-accounts');
        return this.httpClient
            .get<YukiMarGlAccount[]>(`${environment.baseUrl}/yuki/mar-accounts`, this.httpOptions)
            .pipe(
                tap((accounts) => {
                    console.log('[MAR-LINKING][Service] API /mar-accounts response', {
                        count: Array.isArray(accounts) ? accounts.length : 0,
                        sample: Array.isArray(accounts) ? accounts.slice(0, 10) : accounts,
                    });
                    this.setCachedMarAccounts(accounts);
                }),
            );
    }

    upsertMarAccountMapping(accountNumber: string, accountName: string, overheadCategoryId: string | null): Observable<YukiMarGlAccount> {
        console.log('[MAR-LINKING][Service] upsert mapping request', { accountNumber, accountName, overheadCategoryId });
        return this.httpClient.put<YukiMarGlAccount>(
            `${environment.baseUrl}/yuki/mar-accounts/mapping`,
            {
                accountNumber,
                accountName,
                overheadCategoryId,
            },
            this.httpOptions,
        ).pipe(
            tap((response) => {
                console.log('[MAR-LINKING][Service] upsert mapping response', response);
            }),
        );
    }

    private getMarAccountMappings(): Observable<YukiMarGlAccount[]> {
        console.log('[MAR-LINKING][Service] loading mappings from /yuki/mar-accounts/mapping');
        return this.httpClient.get<YukiMarGlAccount[]>(`${environment.baseUrl}/yuki/mar-accounts/mapping`, this.httpOptions).pipe(
            tap((mappings) => {
                console.log('[MAR-LINKING][Service] mappings response', {
                    count: Array.isArray(mappings) ? mappings.length : 0,
                    sample: Array.isArray(mappings) ? mappings.slice(0, 10) : mappings,
                });
            }),
            catchError(() => {
                console.warn('[MAR-LINKING][Service] mappings endpoint failed, falling back to /yuki/mar-accounts');
                return this.httpClient.get<YukiMarGlAccount[]>(`${environment.baseUrl}/yuki/mar-accounts`, this.httpOptions).pipe(
                    map((accounts) =>
                        (accounts || []).map((account) => ({
                            accountNumber: account?.accountNumber,
                            accountName: account?.accountName,
                            overheadCategoryId: account?.overheadCategoryId ?? null,
                        })),
                    ),
                );
            }),
        );
    }

    private getCachedMarAccounts(): YukiMarGlAccount[] | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }

        try {
            const rawValue = localStorage.getItem(this.marAccountsCacheKey);
            if (!rawValue) {
                return null;
            }

            const payload = JSON.parse(rawValue) as { timestamp?: number; data?: unknown };
            if (!Array.isArray(payload?.data) || typeof payload?.timestamp !== 'number') {
                localStorage.removeItem(this.marAccountsCacheKey);
                return null;
            }

            const isExpired = Date.now() - payload.timestamp > this.marAccountsCacheTtlMs;
            if (isExpired) {
                localStorage.removeItem(this.marAccountsCacheKey);
                return null;
            }

            return payload.data as YukiMarGlAccount[];
        } catch {
            localStorage.removeItem(this.marAccountsCacheKey);
            return null;
        }
    }

    private setCachedMarAccounts(accounts: YukiMarGlAccount[]): void {
        if (typeof localStorage === 'undefined' || !Array.isArray(accounts)) {
            return;
        }

        const cacheableAccounts = accounts.map((account) => ({
            accountNumber: account?.accountNumber,
            accountName: account?.accountName,
            overheadCategoryId: null,
        } as YukiMarGlAccount));

        try {
            localStorage.setItem(
                this.marAccountsCacheKey,
                JSON.stringify({
                    timestamp: Date.now(),
                    data: cacheableAccounts,
                }),
            );
            console.log('[MAR-LINKING][Service] cached base MAR accounts', { count: cacheableAccounts.length });
        } catch {
            return;
        }
    }

    private applyMarAccountMappings(accounts: YukiMarGlAccount[], mappings: YukiMarGlAccount[]): YukiMarGlAccount[] {
        const mappingByAccountNumber = new Map<string, string | null>();
        mappings.forEach((mapping) => {
            const key = this.normalizeAccountNumber(mapping?.accountNumber);
            if (!key) {
                return;
            }
            mappingByAccountNumber.set(key, mapping?.overheadCategoryId ?? null);
        });

        console.log('[MAR-LINKING][Service] applying mappings', {
            accountsCount: Array.isArray(accounts) ? accounts.length : 0,
            mappingsCount: mappingByAccountNumber.size,
        });

        return accounts.map((account) => {
            const key = this.normalizeAccountNumber(account?.accountNumber);
            return {
                ...account,
                overheadCategoryId: key ? mappingByAccountNumber.get(key) ?? null : null,
            };
        });
    }

    private normalizeAccountNumber(accountNumber: string | null | undefined): string {
        return String(accountNumber || '').trim();
    }
}
